import { useEffect, useState } from 'react';
import api from '../../api';

/**
 * The latest health report of every member, plus gateway liveness for the members that have
 * a gateway.
 *
 * Three health states, never two: a member with no report is `null` — the health job only
 * writes while a device is in service, so an absent report means "not reporting", not
 * "healthy". Gateway health fails open: FCR-D members characteristically have no gateway
 * (`POST /gateways/by-device-ids` says which do), and a 404 on a gateway read is "no gateway",
 * not a fault.
 *
 * @param {string[]} deviceIds
 * @returns {{
 *   healthByDevice: Map<string, {status: string|null, reportedAt: string|null, alertCount: number}|null>,
 *   gatewayByDevice: Map<string, {status: string|null, lastHeartbeat: string|null}>,
 *   isReady: boolean,
 * }}
 */
const useMemberStatus = deviceIds => {
    const key = deviceIds.join(',');
    const [state, setState] = useState({ healthByDevice: new Map(), gatewayByDevice: new Map(), isReady: false });

    useEffect(() => {
        if (deviceIds.length === 0) {
            setState({ healthByDevice: new Map(), gatewayByDevice: new Map(), isReady: true });
            return undefined;
        }
        let active = true;
        setState(s => ({ ...s, isReady: false }));

        const load = async () => {
            const [healthResults, gatewaysRes] = await Promise.all([
                Promise.all(deviceIds.map(id => api.get_device_health_report(id).catch(() => null))),
                api.get_gateways_among_devices(deviceIds).catch(() => null),
            ]);
            const healthByDevice = new Map();
            deviceIds.forEach((id, i) => {
                const health = healthResults[i]?.data?.health ?? null;
                healthByDevice.set(
                    id,
                    health
                        ? {
                              status: health.status ?? null,
                              reportedAt: health.end ?? health.timestamp ?? null,
                              alertCount: (healthResults[i]?.data?.alerts ?? []).length,
                          }
                        : null
                );
            });

            const gatewayDeviceIds = (gatewaysRes?.data?.device_ids ?? []).filter(id => deviceIds.includes(id));
            const gatewayHealths = await Promise.all(gatewayDeviceIds.map(id => api.get_device_gateway_health(id).catch(() => null)));
            const gatewayByDevice = new Map();
            gatewayDeviceIds.forEach((id, i) => {
                const health = gatewayHealths[i]?.data ?? null;
                gatewayByDevice.set(id, {
                    status: health?.status ? String(health.status).toUpperCase() : null,
                    lastHeartbeat: health?.last_heartbeat ?? null,
                });
            });

            if (active) {
                setState({ healthByDevice, gatewayByDevice, isReady: true });
            }
        };

        load();
        return () => {
            active = false;
        };
        // `key` is the identity of `deviceIds`; the array itself is rebuilt per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return state;
};

export default useMemberStatus;
